/**
 * Route parsing.
 *
 * Explains routing behavior, guards, and response semantics.
 */
import { stripLocalePrefix } from '../../i18n/shopLocale'
import type { Route } from '../types'
import { parsePathSegments } from './pathUtils'
import { normalizeProductPublicCode } from './productPaths'

const previewIdPattern = /^[a-f0-9]{24}$/i

const parsePreviewId = (search: string) => {
  const value = new URLSearchParams(search).get('preview_id')?.trim() ?? ''
  return previewIdPattern.test(value) ? value : undefined
}

/**
 * Parse route.
 *
 * Explains routing behavior, guards, and response semantics.
 */
export const parseRoute = (pathname: string, search = ''): Route => {
  const { pathnameWithoutLocale } = stripLocalePrefix(pathname)
  const trimmed = pathnameWithoutLocale.replace(/^\/+|\/+$/g, '')
  const previewId = parsePreviewId(search)

  if (!trimmed) return { mode: 'home' }
  if (trimmed.startsWith('error/')) {
    const maybeCode = Number(trimmed.slice('error/'.length).split('/')[0])
    if (Number.isInteger(maybeCode) && maybeCode >= 100 && maybeCode <= 599) {
      return { mode: 'error', code: maybeCode }
    }
    return { mode: 'error', code: 500 }
  }
  if (trimmed.startsWith('preview/')) {
    const id = trimmed.slice('preview/'.length)
    return id ? { mode: 'preview', id } : { mode: 'home' }
  }
  if (trimmed.startsWith('products/')) {
    const pathSegments = parsePathSegments(trimmed.slice('products/'.length))
    const slug = pathSegments[pathSegments.length - 1]
    return slug ? { mode: 'product', slug, previewId } : { mode: 'home' }
  }
  if (trimmed.startsWith('dp/')) {
    const segments = parsePathSegments(trimmed.slice('dp/'.length))
    if (segments.length !== 1) return { mode: 'home' }
    const code = segments[0]
    const publicCode = normalizeProductPublicCode(code)
    return publicCode ? { mode: 'product', slug: publicCode, publicCode, previewId } : { mode: 'home' }
  }

  const directPublicCode = normalizeProductPublicCode(trimmed)
  if (directPublicCode) {
    return { mode: 'product', slug: directPublicCode, publicCode: directPublicCode, previewId }
  }

  return { mode: 'page', slug: trimmed }
}
